import { COLORS } from "../shared/constants";
import { useState, useMemo } from "react";
import * as api from "../api";

export default function ScheduleVisitModal({ patient, patients, onClose, onCreated }) {
  // Patient selection — pre-filled if passed, otherwise searchable dropdown
  const [patientSearch, setPatientSearch] = useState(patient ? `${patient.first_name} ${patient.last_name}` : "");
  const [selectedPatient, setSelectedPatient] = useState(patient || null);
  const [showDropdown, setShowDropdown] = useState(false);

  // Appointment details
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState(30);

  // Recurring
  const [recurring, setRecurring] = useState(false);
  const [recurEvery, setRecurEvery] = useState(1);
  const [recurUnit, setRecurUnit] = useState("week");
  const [recurDays, setRecurDays] = useState([]); // [0=Sun, 1=Mon, ...]
  const [recurEnds, setRecurEnds] = useState("after");
  const [recurCount, setRecurCount] = useState(36);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dayLabels = ["S", "M", "T", "W", "T", "F", "S"];

  // Recent patients (sorted by patient_number descending = most recently added)
  const recentPatients = useMemo(() => {
    if (!patients) return [];
    return [...patients].sort((a, b) => (b.patient_number || 0) - (a.patient_number || 0)).slice(0, 10);
  }, [patients]);

  // Filter patients by search text, or show recent if empty
  const dropdownPatients = useMemo(() => {
    if (!patients) return [];
    if (!patientSearch.trim()) return recentPatients;
    const q = patientSearch.toLowerCase();
    return patients.filter((p) => {
      const full = `${p.first_name} ${p.last_name}`.toLowerCase();
      const num = String(p.patient_number);
      return full.includes(q) || num.includes(q);
    }).slice(0, 10);
  }, [patients, patientSearch, recentPatients]);

  const selectPatient = (p) => {
    setSelectedPatient(p);
    setPatientSearch(`${p.first_name} ${p.last_name}`);
    setShowDropdown(false);
  };

  const toggleDay = (dayIndex) => {
    setRecurDays((prev) =>
      prev.includes(dayIndex) ? prev.filter((d) => d !== dayIndex) : [...prev, dayIndex]
    );
  };

  const handleCreate = async () => {
    if (!date || !time) { setError("Date and time are required."); return; }
    if (!selectedPatient) { setError("Please select a patient."); return; }
    setSaving(true); setError(null);

    try {
      const summary = `${selectedPatient.first_name} ${selectedPatient.last_name} - TMS`;
      const description = `Patient #${selectedPatient.patient_number}`;

      if (recurring && recurDays.length > 0 && recurCount > 0) {
        // Create multiple events
        const created = [];
        const startBase = new Date(`${date}T${time}:00`);
        let current = new Date(startBase);
        let count = 0;

        // Find the first matching day on or after the selected date
        while (count < recurCount) {
          const dayOfWeek = current.getDay();
          if (recurDays.includes(dayOfWeek)) {
            const startStr = `${current.toISOString().slice(0, 10)}T${time}:00`;
            const endDate = new Date(current.getTime());
            endDate.setHours(parseInt(time.split(":")[0]), parseInt(time.split(":")[1]) + duration);
            const endStr = `${endDate.toISOString().slice(0, 10)}T${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}:00`;

            const ev = await api.calendarCreateEvent({
              summary,
              description,
              start_time: startStr,
              end_time: endStr,
            });
            created.push(ev.event || ev);
            count++;
          }
          // Advance to next day
          current.setDate(current.getDate() + 1);
          // If using weekly intervals > 1, skip weeks after completing a week cycle
          if (recurUnit === "week" && recurEvery > 1 && current.getDay() === 0) {
            current.setDate(current.getDate() + 7 * (recurEvery - 1));
          }
        }

        if (onCreated && created.length > 0) onCreated(created[0]);
        onClose();
      } else {
        // Single event
        const startTime = `${date}T${time}:00`;
        const startDate = new Date(`${date}T${time}:00`);
        const endDate = new Date(startDate.getTime() + duration * 60000);
        const endHH = String(endDate.getHours()).padStart(2, "0");
        const endMM = String(endDate.getMinutes()).padStart(2, "0");
        const endTime = `${date}T${endHH}:${endMM}:00`;
        // If end crosses midnight, use next day
        const endDateStr = endDate.getTime() > new Date(`${date}T23:59:59`).getTime()
          ? endDate.toISOString().slice(0, 10)
          : date;
        const finalEndTime = `${endDateStr}T${endHH}:${endMM}:00`;

        const ev = await api.calendarCreateEvent({
          summary,
          description,
          start_time: startTime,
          end_time: finalEndTime,
        });
        if (onCreated) onCreated(ev.event || ev);
        onClose();
      }
    } catch (e) {
      setError(e.error || "Failed to create appointment.");
    }
    setSaving(false);
  };

  const minDate = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: COLORS.surface, borderRadius: "14px", padding: "28px", width: "90%", maxWidth: "460px", boxShadow: "0 8px 32px rgba(0,0,0,0.15)", maxHeight: "90vh", overflow: "auto" }}>
        <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, marginBottom: "16px" }}>
          Schedule Visit
        </h3>

        {error && (
          <div style={{ padding: "8px 12px", borderRadius: "6px", marginBottom: "12px", fontSize: "12px", background: COLORS.dangerLight, color: COLORS.danger, border: `1px solid ${COLORS.dangerBorder}` }}>
            {error}
          </div>
        )}

        {/* Patient Search */}
        <div style={{ marginBottom: "16px", position: "relative" }}>
          <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Patient</label>
          <input
            type="text"
            value={patientSearch}
            onChange={(e) => {
              setPatientSearch(e.target.value);
              setSelectedPatient(null);
              setShowDropdown(true);
            }}
            onFocus={() => { if (!selectedPatient) setShowDropdown(true); }}
            placeholder="Search by name or ID..."
            style={{ width: "100%", height: "40px", padding: "0 12px", borderRadius: "8px", border: `1px solid ${selectedPatient ? "#2ECC71" : COLORS.border}`, fontSize: "14px", color: COLORS.text, background: COLORS.surface, boxSizing: "border-box" }}
          />
          {selectedPatient && (
            <span style={{ position: "absolute", right: "12px", top: "28px", fontSize: "12px", color: "#2ECC71", fontWeight: "600" }}>
              #{selectedPatient.patient_number}
            </span>
          )}

          {/* Dropdown */}
          {showDropdown && !selectedPatient && dropdownPatients.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 10, maxHeight: "240px", overflow: "auto", marginTop: "2px" }}>
              {!patientSearch.trim() && (
                <div style={{ padding: "6px 14px", fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: `1px solid ${COLORS.borderLight}` }}>
                  Recent Patients
                </div>
              )}
              {dropdownPatients.map((p) => (
                <div key={p.id} onClick={() => selectPatient(p)}
                  style={{ padding: "10px 14px", cursor: "pointer", fontSize: "13px", color: COLORS.text, borderBottom: `1px solid ${COLORS.borderLight}` }}
                  onMouseEnter={(e) => e.currentTarget.style.background = COLORS.primaryLight}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <span style={{ fontWeight: "500" }}>{p.first_name} {p.last_name}</span>
                  <span style={{ color: COLORS.textSecondary, marginLeft: "8px" }}>#{p.patient_number}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Date */}
        <div style={{ marginBottom: "14px" }}>
          <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Date</label>
          <input type="date" value={date} min={minDate} onChange={(e) => setDate(e.target.value)}
            style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "14px", color: COLORS.text, background: COLORS.surface, boxSizing: "border-box" }} />
        </div>

        {/* Time + Duration */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
          <div>
            <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Time</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "14px", color: COLORS.text, background: COLORS.surface, boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Duration</label>
            <select value={duration} onChange={(e) => setDuration(parseInt(e.target.value))}
              style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "14px", color: COLORS.text, background: COLORS.surface }}>
              <option value={15}>15 min</option>
              <option value={20}>20 min</option>
              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
              <option value={50}>50 min</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
            </select>
          </div>
        </div>

        {/* Recurring */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: COLORS.text, cursor: "pointer", fontWeight: "500" }}>
            <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)}
              style={{ width: "18px", height: "18px", accentColor: COLORS.primary }} />
            Recurring
          </label>

          {recurring && (
            <div style={{ marginTop: "12px", padding: "14px", background: COLORS.bg, borderRadius: "8px", border: `1px solid ${COLORS.borderLight}` }}>
              {/* Every X week(s) */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                <span style={{ fontSize: "13px", color: COLORS.text }}>Every</span>
                <select value={recurEvery} onChange={(e) => setRecurEvery(parseInt(e.target.value))}
                  style={{ width: "60px", height: "34px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface }}>
                  {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <select value={recurUnit} onChange={(e) => setRecurUnit(e.target.value)}
                  style={{ width: "100px", height: "34px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface }}>
                  <option value="week">week</option>
                  <option value="day">day</option>
                </select>
              </div>

              {/* Day picker (for weekly) */}
              {recurUnit === "week" && (
                <div style={{ display: "flex", gap: "4px", marginBottom: "12px" }}>
                  {dayLabels.map((label, i) => (
                    <button key={i} onClick={() => toggleDay(i)}
                      style={{
                        width: "38px", height: "38px", borderRadius: "8px",
                        border: recurDays.includes(i) ? "2px solid " + COLORS.primary : `1px solid ${COLORS.border}`,
                        background: recurDays.includes(i) ? COLORS.primary : COLORS.surface,
                        color: recurDays.includes(i) ? "#fff" : COLORS.text,
                        fontSize: "13px", fontWeight: "600", cursor: "pointer",
                      }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {/* Ends after X events */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "13px", color: COLORS.text }}>Ends</span>
                <select value={recurEnds} onChange={(e) => setRecurEnds(e.target.value)}
                  style={{ width: "80px", height: "34px", padding: "0 6px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface }}>
                  <option value="after">after</option>
                </select>
                <input type="number" value={recurCount} min={1} max={100}
                  onChange={(e) => setRecurCount(parseInt(e.target.value) || 1)}
                  style={{ width: "60px", height: "34px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface, textAlign: "center" }} />
                <span style={{ fontSize: "13px", color: COLORS.text }}>events</span>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        {recurring && recurDays.length > 0 && date && (
          <div style={{ padding: "10px 14px", borderRadius: "8px", background: COLORS.primaryLight, marginBottom: "16px", fontSize: "12px", color: COLORS.primary, lineHeight: "1.5" }}>
            {recurCount} appointments · Every {recurEvery > 1 ? `${recurEvery} weeks` : "week"} on {recurDays.sort().map((d) => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d]).join(", ")} · Starting {date}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button onClick={onClose}
            style={{ padding: "10px 20px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "13px", cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={handleCreate} disabled={saving || !date || !selectedPatient}
            style={{ padding: "10px 20px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#fff", fontSize: "13px", fontWeight: "600", cursor: saving ? "wait" : "pointer", opacity: saving || !date || !selectedPatient ? 0.7 : 1 }}>
            {saving ? (recurring ? "Scheduling..." : "Scheduling...") : recurring && recurDays.length > 0 ? `Schedule ${recurCount} Visits` : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
